/**
 * Firm "administering X" context bar (first-class tenancy).
 *
 * Persistent while a firm is acting on a client. Mirrors the impersonation
 * banner's mechanics (a sticky bar read from a Zustand store that the api()
 * client also reads for its token), BUT this is the firm's everyday work — not
 * break-glass — so it is deliberately CALM: brand-tint, not the alarming red of
 * impersonation. It answers, at a glance: whose data am I in, what may I do, how
 * do I leave. A 'read' grant shows a "View only" chip so the constraint travels
 * with the identity.
 *
 * All colour is via brand tokens (var(--primary-*) / var(--accent-*) alias the
 * white-label brand-primary / brand-accent) — never literal hex — so it reskins
 * per firm.
 */

import { useNavigate } from '@tanstack/react-router';
import { displayPersonName } from '../lib/format/person-name';
import { useActingContext, useFirmActControls } from '../lib/acting/context';
import { Icon } from './icon';

export function FirmActBar() {
  // ARC-01: posture from the acting context, never from a store.
  const acting = useActingContext();
  if (acting.posture !== 'acting-as-client') return null;
  return <ActiveFirmActBar />;
}

function ActiveFirmActBar() {
  const acting = useActingContext();
  const { endFirmAct: clear } = useFirmActControls();
  const navigate = useNavigate();
  const isView = acting.standing === 'read';

  async function handleExit() {
    // STORE clear AFTER the navigate. The bar renders under the /app layout
    // guard (routes/app/_layout.tsx). That guard SKIPS its operator-session MFA
    // step-up check only while the firm-act context is `active`; the moment it
    // reads `none`, it re-runs the step-up gate on the operator's OWN Supabase
    // session — legitimately aal1 here (the stepped-up credential was the act-as
    // token, not the operator session). So clearing the store while still
    // mounted on /app would re-render the guard, see no act-as + aal1, and bounce
    // to /auth/mfa-verify, racing the not-yet-committed navigate. AWAIT the
    // navigation OUT of /app first, THEN clear the store: while the await is
    // pending the store is still `active` (guard tolerant); by the time we clear,
    // AppLayout has unmounted. /admin's own beforeLoad also clearActAsContext()s
    // — this explicit clear is the belt-and-suspenders twin that keeps the
    // ordering contract legible (and testable) at this call site.
    //
    // ⛔ THERE USED TO BE A `qc.clear()` ON THE LINE ABOVE, AND ITS REMOVAL IS
    // THE POINT OF ARC-01 T6 (2026-08-27). It was one of two hand-written
    // mitigations for a cache principal that knew nothing about posture; the
    // principal now IS the acting context's `principalKey`
    // (lib/query-client.ts), recomputed from a subscription to this very store.
    // So `clear()` below re-points the cache by itself, and it does it by
    // RE-KEYING rather than evicting: the client's entries become unreachable
    // instead of deleted, and the operator's own console data — which
    // `qc.clear()` threw away along with the client's — survives and is warm on
    // arrival.
    //
    // Removing it also retires the ordering half of QA F6 ("Client Book hangs
    // ~40s after Exit to clients"). That hang was `qc.clear()` DESTROYING the
    // Client Book's just-mounted ['firm-clients','list'] query mid-flight,
    // leaving an orphaned observer stuck `isLoading`; the fix at the time was to
    // clear before navigating. A re-key destroys nothing, so there is no longer
    // anything to order, and the regression cannot recur by construction.
    //
    // The one visible cost, stated: the Client Book mounts while this store is
    // still set, so its own queries are filed under the CLIENT's principal for
    // the moment between the navigate and the clear. They are fetched with
    // `useOperatorSession: true`, so the request is still the operator's own and
    // the data is correct — only the cache filing is off, and a later re-render
    // costs one refetch. ARC-01 T7 (`audience` per call) is the real fix.
    await navigate({ to: '/admin/client-book' });
    clear();
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="firm-act-bar"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        flexWrap: 'wrap',
        padding: '9px 16px',
        background: 'var(--primary-100)',
        borderBottom: '1px solid var(--primary-200)',
        color: 'var(--ink-2)',
        fontSize: 13.5,
      }}
    >
      <span style={{ color: 'var(--ink-3)' }}>Administering</span>
      <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>{displayPersonName(acting.subject.displayName)}</strong>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 11,
          fontWeight: 600,
          padding: '3px 9px',
          borderRadius: 999,
          background: 'var(--surface)',
          color: isView ? 'var(--ink-2)' : 'var(--primary-700)',
          border: '1px solid ' + (isView ? 'var(--hairline)' : 'var(--primary-200)'),
        }}
      >
        {isView ? <Icon name="lock" size={12} /> : null}
        {isView ? 'View only' : 'Managing'}
      </span>
      <span style={{ flex: 1 }} />
      <button
        type="button"
        onClick={() => void handleExit()}
        className="btn-secondary"
        data-testid="firm-act-exit"
        style={{ fontSize: 12.5 }}
      >
        Exit to clients
      </button>
    </div>
  );
}
